/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DataGridPro as DataGrid } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";

/**
 * Format a numeric value for display in a DataGrid cell.
 *
 * @param {number|null} value - The value to format
 * @param {"currency"|"number"|"rate"} format - The format type
 * @returns {string} Formatted value or "-" for null
 */
const formatValue = (value, format) => {
  if (value === null || value === undefined) return "-";
  switch (format) {
    case "currency":
      return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case "rate":
      return `$${value.toFixed(4)}`;
    case "number":
    default:
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  }
};

/**
 * Assign CSS class names to rows based on their type for styling.
 */
const getRowClassName = (params) => {
  const type = params.row._type;
  const classes = [];
  if (type === "total") classes.push("row-total");
  if (type === "sub") classes.push("row-sub");
  // separator: header rows after the first row, or total rows
  if ((type === "header" && params.row.id > 0) || type === "total") {
    classes.push("row-separator");
  }
  return classes.join(" ");
};

/**
 * DataGrid sx overrides for row-type styling (theme-aware).
 */
const getDataGridSx = (theme) => ({
  border: "none",
  // Total row: light grey background, bold text
  "& .row-total": {
    backgroundColor: theme.palette.grey[50],
  },
  "& .row-total .MuiDataGrid-cell": {
    fontWeight: 700,
  },
  // Sub rows: indent the label cell
  "& .cell-sub-label": {
    paddingLeft: "40px",
  },
  // Separator: thicker top border for group boundaries
  "& .row-separator .MuiDataGrid-cell": {
    borderTop: `2px solid ${theme.palette.grey[400]}`,
  },
  // Hide the footer (belt-and-suspenders with hideFooter prop)
  "& .MuiDataGrid-footerContainer": {
    display: "none",
  },
});

/**
 * A single report table section with title, sub-label, and DataGridPro table.
 */
const ReportTableSection = ({ title, subLabel, table, format }) => {
  const theme = useTheme();

  // Build DataGrid column definitions from table.columns
  const columns = useMemo(() => {
    if (!table) return [];
    const lastCol = table.columns[table.columns.length - 1];
    return [
      {
        field: "label",
        headerName: " ",
        renderHeader: () => "",
        // Not flex: autosizeOnMount narrows this to the widest label so the
        // value columns keep the Total in view on narrow windows. The width
        // below is the pre-autosize fallback (fits "Baseline Energy (kWh)").
        width: 180,
        sortable: false,
        disableColumnMenu: true,
        cellClassName: (params) => (params.row._type === "sub" ? "cell-sub-label" : ""),
      },
      ...table.columns.map((col) => ({
        field: col,
        headerName: col,
        flex: 1,
        minWidth: 100,
        sortable: false,
        disableColumnMenu: true,
        // renderCell drives the visual display — null → "-"
        renderCell: (params) => formatValue(params.value, format),
        // Bold the last column cell in total rows via cellClassName
        cellClassName: (params) => {
          if (params.row._type === "total" && col === lastCol) {
            return "cell-total-last";
          }
          return "";
        },
      })),
    ];
  }, [table, format]);

  // Transform rows: spread dict values directly into row objects
  const rows = useMemo(
    () =>
      (table?.rows ?? []).map((row, idx) => ({
        id: idx,
        label: row.label,
        _type: row.type,
        ...row.values,
      })),
    [table],
  );

  if (!table) return null;

  return (
    <Box sx={{ mb: 6 }}>
      <Box sx={{ mb: 1 }}>
        <Typography
          variant="h5"
          sx={{ fontSize: "24px", fontWeight: 600, lineHeight: 1.334, color: "text.primary" }}
        >
          {title}
        </Typography>
        {subLabel && (
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: 1.75,
              letterSpacing: "0.15px",
              color: "text.secondary",
            }}
          >
            {subLabel}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: "4px",
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          density="compact"
          autoHeight
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooter
          columnHeaderHeight={52}
          autosizeOnMount
          // includeOutliers: the few long group labels would otherwise be
          // discarded as outliers next to the short indented sub-row labels.
          autosizeOptions={{ columns: ["label"], includeOutliers: true }}
          getRowClassName={getRowClassName}
          sx={getDataGridSx(theme)}
        />
      </Box>
    </Box>
  );
};

ReportTableSection.propTypes = {
  title: PropTypes.string.isRequired,
  subLabel: PropTypes.string,
  table: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.string).isRequired,
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        type: PropTypes.oneOf(["header", "sub", "total", "data"]).isRequired,
        values: PropTypes.object.isRequired,
      }),
    ).isRequired,
  }),
  format: PropTypes.oneOf(["currency", "number", "rate"]),
};

/**
 * PerformanceReportTables renders the three performance report tables:
 * Bill Savings, Utility Usage, and Energy Rates.
 */
const PerformanceReportTables = ({ report }) => {
  if (!report) return null;

  return (
    <Box>
      <ReportTableSection
        title="Bill Savings"
        subLabel="Estimated"
        table={report.bill_savings}
        format="currency"
      />
      <ReportTableSection
        title="Utility Usage"
        subLabel="Estimated"
        table={report.utility_usage}
        format="number"
      />
      <ReportTableSection
        title="Energy Rates"
        subLabel="Actual"
        table={report.energy_rates}
        format="rate"
      />
    </Box>
  );
};

PerformanceReportTables.propTypes = {
  report: PropTypes.shape({
    bill_savings: PropTypes.object,
    utility_usage: PropTypes.object,
    energy_rates: PropTypes.object,
  }),
};

export default PerformanceReportTables;
